import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../utils/cn';
const ToastContext = createContext(null);
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
const variantStyles = {
  success: 'bg-success-50 border-success-200 text-success-800',
  error: 'bg-danger-50 border-danger-200 text-danger-800',
  warning: 'bg-warning-50 border-warning-200 text-warning-800',
  info: 'bg-surface-secondary border-edge-strong text-neutral-800',
};
const variantIcons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};
function ToastItem({ toast, onDismiss }) {
  const timerRef = useRef(undefined);
  useEffect(() => {
    const duration = toast.duration ?? 5000;
    timerRef.current = setTimeout(() => onDismiss(toast.id), duration);
    return () => clearTimeout(timerRef.current);
  }, [toast.id, toast.duration, onDismiss]);
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 w-80 p-4 rounded-lg border shadow-lg animate-in fade-in slide-in-from-right',
        variantStyles[toast.variant],
      )}
    >
      {React.createElement(variantIcons[toast.variant], {
        size: 20,
        className: 'shrink-0 mt-0.5',
        'aria-hidden': true,
      })}
      <p className="text-sm font-medium flex-1">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 text-current opacity-50 hover:opacity-100 transition-opacity"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
let toastCounter = 0;
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((toast) => {
    const id = `toast-${++toastCounter}`;
    setToasts((prev) => [
      ...prev,
      {
        ...toast,
        id,
      },
    ]);
  }, []);
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  return (
    <ToastContext.Provider
      value={{
        toasts,
        addToast,
        removeToast,
      }}
    >
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
